import React, { useCallback, useMemo, useState } from 'react';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Box, IconButton, InputAdornment, Stack, Tooltip, Typography } from '@mui/material';
import { DeleteOutline, Link as LinkIcon, Search } from '@mui/icons-material';
import { Alert, Chip, SelectField, TextField } from 'c-components';
import type { CampaignInsertionOrderPlaceholder, InsertionOrderKey, ScheduleGet } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import { scheduleName } from 'c-main/Components/Schedule/lib';
import useNewCampaignSchedule from 'c-main/Components/Schedule/useNewCampaignSchedule';
import type { DspTargeting } from './useDspTargeting';
import type { InsertionOrderTableFilters, InsertionOrderTableRow } from './lib';
import {
    attachedByLabels,
    filterInsertionOrders,
    insertionOrderKey,
    isMissingTarget,
    targetCoverage,
} from './lib';
import {
    CpmCell,
    ImpressionsTargetCell,
    ImpressionsTargetText,
    MissingTargetMarker,
    PlaceholderLabelCell,
} from './InsertionOrderDetailCells';

type Props = {
    campaignId: number;
    targeting: DspTargeting;
    schedules: ScheduleGet[];
    /** Keys of the ticked rows. Owned by the container, because ＋/－ on the right rail read it. */
    selectedKeys: string[];
    onSelectionChange: (keys: string[]) => void;
    /** The toolbar buttons -- supplied by the container, which owns the dialogs they open. */
    toolbar?: React.ReactNode;
    /** CPM and the impressions target: both are the insertion order's details. */
    canEditDetails: boolean;
    canSchedule: boolean;
    /** A confirmation for the page's notice bar. */
    onNotice?: (message: string) => void;
    /** UL-8136 -- a planned line's Link action (opens the selection dialog in link mode). */
    onLinkPlaceholder?: (placeholder: CampaignInsertionOrderPlaceholder) => void;
    /** UL-8136 -- a planned line's Delete action (asks for confirmation first). */
    onDeletePlaceholder?: (placeholder: CampaignInsertionOrderPlaceholder) => void;
};

const ANY = 'any';

const emptyFilters: InsertionOrderTableFilters = {
    customer: null,
    advertiser: null,
    nameContains: '',
    scheduleId: null,
};

const formatDate = (value: string | null) => (value ? value.slice(0, 10) : '—');

/**
 * UL-7912 -- the left panel of a DSP campaign's schedule page.
 *
 * Insertion orders, not displays or packs. Every column is read from the catalogue or from how the
 * row was attached, apart from CPM and the impressions target, which the user types.
 *
 * UL-8136 -- planned lines (placeholders) are listed above the insertion orders, marked "Awaiting
 * insertion order", with an editable label and target, and Link / Delete actions.
 */
const InsertionOrderTable: React.FC<Props> = ({
    campaignId,
    targeting,
    schedules,
    selectedKeys,
    onSelectionChange,
    toolbar,
    canEditDetails,
    canSchedule,
    onNotice,
    onLinkPlaceholder,
    onDeletePlaceholder,
}) => {
    const t = useCommonTranslation();
    const { setExpandedScheduleId, setScheduleSearch } = useNewCampaignSchedule(campaignId);
    const [filters, setFilters] = useState<InsertionOrderTableFilters>(emptyFilters);

    const {
        insertionOrders,
        placeholderRows,
        placeholderError,
        rulesById,
        isLoading,
        loadError,
        cpmError,
        targetError,
        scheduleError,
    } = targeting;

    // UL-7967 -- from every attached insertion order, whatever the filters show.
    const coverage = useMemo(() => targetCoverage(insertionOrders), [insertionOrders]);

    const schedulesById = useMemo(() => new Map(schedules.map(s => [s.id, s])), [schedules]);

    const rows = useMemo<InsertionOrderTableRow[]>(
        () => [
            ...filterInsertionOrders(placeholderRows, filters),
            ...filterInsertionOrders(insertionOrders, filters),
        ],
        [insertionOrders, placeholderRows, filters],
    );

    // Filter options come from what is attached, so a filter can never offer something that
    // matches nothing on this campaign.
    const customerOptions = useMemo(
        () => [
            { label: t('Modules.Main.Campaigns.CampaignSchedule.dsp.filters.any'), value: ANY },
            ...[...new Set(insertionOrders.map(io => io.customer))]
                .sort()
                .map(c => ({ label: c, value: c })),
        ],
        [insertionOrders, t],
    );

    const advertiserOptions = useMemo(
        () => [
            { label: t('Modules.Main.Campaigns.CampaignSchedule.dsp.filters.any'), value: ANY },
            ...[...new Set(insertionOrders.map(io => io.advertiser).filter(Boolean) as string[])]
                .sort()
                .map(a => ({ label: a, value: a })),
        ],
        [insertionOrders, t],
    );

    const scheduleOptions = useMemo(
        () => [
            { label: t('Modules.Main.Campaigns.CampaignSchedule.dsp.filters.any'), value: ANY },
            ...schedules.map(s => ({ label: scheduleName(s, t), value: String(s.id) })),
        ],
        [schedules, t],
    );

    const { updateImpressionsTarget } = targeting;
    const saveImpressionsTarget = useCallback(
        async (key: InsertionOrderKey, target: number | null) => {
            const ok = await updateImpressionsTarget(key, target);
            if (ok) {
                onNotice?.(
                    t('Modules.Main.Campaigns.CampaignSchedule.dsp.impressionsTarget.saved'),
                );
            }
            return ok;
        },
        [onNotice, t, updateImpressionsTarget],
    );

    const { updatePlaceholder } = targeting;
    const savePlaceholderTarget = useCallback(
        (placeholderId: number) => async (_key: InsertionOrderKey, target: number | null) => {
            const ok = await updatePlaceholder(placeholderId, { impressions_target: target });
            if (ok) {
                onNotice?.(
                    t('Modules.Main.Campaigns.CampaignSchedule.dsp.impressionsTarget.saved'),
                );
            }
            return ok;
        },
        [onNotice, t, updatePlaceholder],
    );

    const focusSchedule = useCallback(
        (schedule: ScheduleGet) => {
            setExpandedScheduleId(schedule.id);
            setScheduleSearch(schedule.name);
        },
        [setExpandedScheduleId, setScheduleSearch],
    );

    const columns = useMemo<GridColDef<InsertionOrderTableRow>[]>(
        () => [
            {
                field: 'insertion_order_name',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.name'),
                flex: 2,
                minWidth: 220,
                valueGetter: ({ row }) => row.insertion_order_name ?? row.insertion_order_id,
                renderCell: ({ row, formattedValue }) => {
                    const { placeholder } = row;
                    if (!placeholder) return formattedValue;
                    return canEditDetails ? (
                        <PlaceholderLabelCell
                            key={placeholder.label}
                            label={placeholder.label}
                            onSave={label => updatePlaceholder(placeholder.id, { label })}
                        />
                    ) : (
                        <Typography variant="body2">{placeholder.label}</Typography>
                    );
                },
            },
            {
                field: 'insertion_order_id',
                headerName: t(
                    'Modules.Main.Campaigns.CampaignSchedule.dsp.columns.insertionOrderId',
                ),
                flex: 1,
                minWidth: 190,
                // A planned line has no insertion order yet: its key is the API's reserved one.
                renderCell: ({ row, formattedValue }) =>
                    row.placeholder ? (
                        <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={t(
                                'Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.awaiting',
                            )}
                        />
                    ) : (
                        formattedValue
                    ),
            },
            {
                field: 'advertiser',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.advertiser'),
                flex: 1,
                minWidth: 120,
            },
            {
                field: 'min_date',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.minDate'),
                width: 110,
                valueFormatter: ({ value }) => formatDate(value),
            },
            {
                field: 'max_date',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.maxDate'),
                width: 110,
                valueFormatter: ({ value }) => formatDate(value),
            },
            {
                field: 'cpm',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.cpm'),
                width: 140,
                sortable: false,
                renderCell: ({ row }) =>
                    canEditDetails && !row.placeholder ? (
                        <CpmCell row={row} onSave={targeting.updateCpm} />
                    ) : (
                        <Typography variant="body2">{row.cpm ?? '—'}</Typography>
                    ),
            },
            {
                field: 'impressions_target',
                headerName: t(
                    'Modules.Main.Campaigns.CampaignSchedule.dsp.columns.impressionsTarget',
                ),
                width: 220,
                sortable: false,
                // UL-7967 -- an insertion order with no target is flagged in its own cell.
                renderCell: ({ row }) => (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        {canEditDetails ? (
                            <ImpressionsTargetCell
                                row={row}
                                onSave={
                                    row.placeholder
                                        ? savePlaceholderTarget(row.placeholder.id)
                                        : saveImpressionsTarget
                                }
                            />
                        ) : (
                            <ImpressionsTargetText value={row.impressions_target} />
                        )}
                        {isMissingTarget(row) && <MissingTargetMarker />}
                    </Stack>
                ),
            },
            {
                field: 'attached_by',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.attachedBy'),
                flex: 1.5,
                minWidth: 200,
                sortable: false,
                valueGetter: ({ row }) =>
                    attachedByLabels(row.parameter_ids, rulesById).join(' · '),
            },
            {
                field: 'schedule',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.dsp.columns.schedule'),
                flex: 1.5,
                minWidth: 220,
                sortable: false,
                renderCell: ({ row }) => {
                    if (row.schedule_id === null) {
                        return (
                            <Typography variant="caption" color="text.secondary">
                                {t('Modules.Main.Campaigns.CampaignSchedule.dsp.schedule.noDates')}
                            </Typography>
                        );
                    }

                    const schedule = schedulesById.get(row.schedule_id);
                    if (!schedule) return null;

                    const isDefault = schedule.is_default === true;

                    return (
                        <Chip
                            size="small"
                            onClick={() => focusSchedule(schedule)}
                            label={
                                isDefault
                                    ? t(
                                          'Modules.Main.Campaigns.CampaignSchedule.dsp.schedule.defaultLabel',
                                      )
                                    : scheduleName(schedule, t)
                            }
                            // ✕ returns this row to the Default, as removing a real schedule does. The
                            // Default itself has nothing to return to, so it has no ✕.
                            onDelete={
                                !isDefault && canSchedule
                                    ? () =>
                                          targeting.returnToDefault(
                                              [
                                                  {
                                                      customer: row.customer,
                                                      insertion_order_id: row.insertion_order_id,
                                                  },
                                              ],
                                              schedule.id,
                                          )
                                    : undefined
                            }
                        />
                    );
                },
            },
            {
                field: 'actions',
                headerName: '',
                width: 96,
                sortable: false,
                renderCell: ({ row }) => {
                    const { placeholder } = row;
                    if (!placeholder || !canEditDetails) return null;
                    const linkLabel = t(
                        'Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.link',
                    );
                    const deleteLabel = t(
                        'Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.delete',
                    );
                    return (
                        <Stack direction="row" onClick={e => e.stopPropagation()}>
                            <Tooltip title={linkLabel}>
                                <IconButton
                                    size="small"
                                    aria-label={linkLabel}
                                    onClick={() => onLinkPlaceholder?.(placeholder)}
                                >
                                    <LinkIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={deleteLabel}>
                                <IconButton
                                    size="small"
                                    color="error"
                                    aria-label={deleteLabel}
                                    onClick={() => onDeletePlaceholder?.(placeholder)}
                                >
                                    <DeleteOutline fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    );
                },
            },
        ],
        [
            t,
            canEditDetails,
            targeting,
            saveImpressionsTarget,
            savePlaceholderTarget,
            updatePlaceholder,
            onLinkPlaceholder,
            onDeletePlaceholder,
            rulesById,
            schedulesById,
            focusSchedule,
            canSchedule,
        ],
    );

    const setFilter = useCallback(
        <K extends keyof InsertionOrderTableFilters>(
            key: K,
            value: InsertionOrderTableFilters[K],
        ) => setFilters(current => ({ ...current, [key]: value })),
        [],
    );

    return (
        <Stack gap={2} sx={{ height: '100%', minHeight: 0 }}>
            {toolbar}

            <Stack direction="row" gap={2} flexWrap="wrap">
                <Box minWidth={160}>
                    <SelectField
                        label={t('Modules.Main.Campaigns.CampaignSchedule.dsp.filters.customer')}
                        value={filters.customer ?? ANY}
                        options={customerOptions}
                        onChange={e =>
                            setFilter(
                                'customer',
                                e.target.value === ANY ? null : String(e.target.value),
                            )
                        }
                    />
                </Box>
                <Box minWidth={160}>
                    <SelectField
                        label={t('Modules.Main.Campaigns.CampaignSchedule.dsp.filters.advertiser')}
                        value={filters.advertiser ?? ANY}
                        options={advertiserOptions}
                        onChange={e =>
                            setFilter(
                                'advertiser',
                                e.target.value === ANY ? null : String(e.target.value),
                            )
                        }
                    />
                </Box>
                <Box minWidth={220}>
                    <TextField
                        label={t(
                            'Modules.Main.Campaigns.CampaignSchedule.dsp.filters.nameContains',
                        )}
                        value={filters.nameContains}
                        onChange={e => setFilter('nameContains', e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Box minWidth={200}>
                    <SelectField
                        label={t('Modules.Main.Campaigns.CampaignSchedule.dsp.filters.schedule')}
                        value={filters.scheduleId === null ? ANY : String(filters.scheduleId)}
                        options={scheduleOptions}
                        onChange={e =>
                            setFilter(
                                'scheduleId',
                                e.target.value === ANY ? null : Number(e.target.value),
                            )
                        }
                    />
                </Box>
            </Stack>

            {loadError && <Alert severity="error">{String(loadError)}</Alert>}
            {scheduleError && <Alert severity="error">{String(scheduleError)}</Alert>}
            {cpmError && <Alert severity="error">{String(cpmError)}</Alert>}
            {placeholderError && <Alert severity="error">{String(placeholderError)}</Alert>}
            {targetError && (
                <Alert severity="error">
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.impressionsTarget.save')}:{' '}
                    {String(targetError)}
                </Alert>
            )}

            {coverage.total > 0 && (
                <Typography
                    variant="body2"
                    color={coverage.withTarget < coverage.total ? 'warning.main' : 'text.secondary'}
                >
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.targetCount', {
                        withTarget: coverage.withTarget,
                        count: coverage.total,
                    })}
                </Typography>
            )}

            <Box sx={{ flex: 1, minHeight: 360 }}>
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    getRowId={row => insertionOrderKey(row)}
                    loading={isLoading}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedKeys}
                    onRowSelectionModelChange={model => onSelectionChange(model.map(String))}
                    pagination
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    density="compact"
                />
            </Box>
        </Stack>
    );
};

export default InsertionOrderTable;
